/**
 * apps/command/src/components/KioskTile.tsx — §15.6's kiosk tile.
 *
 * ============================================================================
 * §3.5, VISIBLY. A TILE NEVER SHOWS A NUMBER WITHOUT SAYING HOW OLD IT IS.
 * ============================================================================
 *
 * "The panel shows last-known state with its timestamp, never a stale value
 * pretending to be live." So the age line is not optional decoration on this
 * tile — it is rendered from the same `telemetry_at` that decides the status
 * pill, and a kiosk with no telemetry at all says so in words instead of
 * drawing an empty frame that reads as a fault in the panel.
 *
 * The status is recomputed from `now` on every render, and `now` ticks every
 * second, so a tile ages by itself: «Sogʻlom» → «Nosoz» → «Oflayn» with nobody
 * refreshing anything (§15.9 "live without refresh").
 */
import { Link } from 'react-router-dom';
import { t } from '../i18n/index.js';
import { ageParts, percent, reading } from '../lib/format.js';
import { statusOf, type MbtKiosk } from '../realtime/useFleet.js';
import { Readout, StatusPill } from '../ui/index.jsx';

export function KioskTile({ kiosk, now }: { kiosk: MbtKiosk; now: Date }): React.JSX.Element {
  const status = statusOf(kiosk, now);
  const telemetryAt = kiosk.telemetry_at ? new Date(kiosk.telemetry_at) : null;
  const age = telemetryAt ? ageParts(telemetryAt, now) : null;

  const disk = percent(reading(kiosk.telemetry, 'health', 'disk_free_pct'));
  const cpu = percent(reading(kiosk.telemetry, 'health', 'cpu_pct'));
  const rtt = reading(kiosk.telemetry, 'network', 'rtt_ms');

  return (
    <Link
      to={`/fleet/${kiosk.id}`}
      className="block rounded border border-hairline bg-panel p-4 transition-colors duration-state hover:bg-panel-raised focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lead font-semibold tabular-nums text-ink-100">{kiosk.short_code}</p>
          <p className="truncate text-micro text-ink-500">{kiosk.mahalla_name}</p>
        </div>
        <StatusPill status={status} label={t(`status.${status}`)} />
      </div>

      <div className="mt-3 border-t border-hairline pt-2">
        {kiosk.telemetry === undefined ? (
          // Not an empty box. §15.10: "a 'no data' state that says WHY there is
          // no data". An unenrolled kiosk and a kiosk that went dark are
          // different facts and a technician reads them differently.
          <p className="py-2 text-micro text-ink-500">
            {kiosk.enrolled ? t('fleet.noTelemetry') : t('fleet.notEnrolled')}
          </p>
        ) : (
          <>
            <Readout
              label={t('fleet.metric.disk')}
              value={disk}
              absent={t('app.unknown')}
              unit={t('unit.percent')}
            />
            <Readout
              label={t('fleet.metric.cpu')}
              value={cpu}
              absent={t('app.unknown')}
              unit={t('unit.percent')}
            />
            <Readout
              label={t('fleet.metric.rtt')}
              value={rtt === null ? null : String(rtt)}
              absent={t('app.unknown')}
              unit={t('unit.ms')}
            />
          </>
        )}
      </div>

      {/* The "as of". Always present, always beside the numbers it dates. */}
      <p className="mt-2 text-micro tabular-nums text-ink-500">
        {age ? t(age.key, { n: age.n }) : t('app.never')}
      </p>
    </Link>
  );
}
